import { IonIcon } from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { helpCircleOutline } from "ionicons/icons";
import { type SignupClaim } from "@/lib/signup/date-groups";
import { getSignupTitle } from "@/lib/utils/scheduling-text";
import { ApplicationSignupDateTab } from "./application-signup.date-tab";
import { ApplicationSignupShiftBox } from "./application-signup.shift-box";

interface Shift {
  id: string;
  title: string;
  timeRange: string;
  date: string;
  isReserveSlot?: boolean;
  /** Jouw aanmelding op deze dienst; zie `lib/signup/date-groups`. */
  claim?: SignupClaim | null;
}

interface DateGroup {
  date: string;
  label: string;
  shifts: Shift[];
  /** Jouw aanmelding op deze dag, of niets als de dag nog vrij is. */
  claim?: SignupClaim | null;
}

interface ApplicationSignupContainerProps {
  /** Grouped shifts by date */
  dateGroups: DateGroup[];
  /** Currently selected shift IDs */
  selectedShiftIds: string[];
  /** Callback when shift selection changes */
  onSelectionChange: (shiftIds: string[]) => void;
  /** Optional help callback */
  onHelpClick?: () => void;
  /** Optional submit callback - if provided, shows submit button */
  onSubmit?: () => void;
  /** Whether submission is in progress */
  isSubmitting?: boolean;
  /** Callback when active tab changes */
  onActiveTabChange?: (index: number) => void;
  /** Scheduling method to determine terminology */
  schedulingMethod?: string;
  /**
   * Optional initial date (YYYY-MM-DD) to open on. When it matches one of the dateGroups, that tab
   * is selected on mount instead of the first one. This lets a tapped "ontdek" card land on its own
   * date's segment. Falls back to the first tab when omitted or when no group matches.
   */
  initialDate?: string;
}

/**
 * Aangemeld of ingepland: twee zinnen, vier standen.
 *
 * `PENDING` en `RESERVE` zijn een vraag die nog open staat, `ACCEPTED` en `CONSUMED` zijn een
 * afspraak. Dat verschil hoort de regel boven de diensten te maken -- "je bent ingepland" tegen
 * iemand die nog op antwoord wacht is een belofte die de planner niet gedaan heeft.
 */
function claimTone(claim: SignupClaim): "applied" | "scheduled" {
  return claim === "ACCEPTED" || claim === "CONSUMED" ? "scheduled" : "applied";
}

/** Returns the index of the dateGroup matching `initialDate`, or 0 as fallback. */
function getInitialTabIndex(dateGroups: DateGroup[], initialDate?: string): number {
  if (!initialDate) {
    return 0;
  }
  const index = dateGroups.findIndex((group) => group.date === initialDate);
  return index >= 0 ? index : 0;
}

export function ApplicationSignupContainer({
  dateGroups,
  selectedShiftIds,
  onSelectionChange,
  onHelpClick,
  onSubmit,
  isSubmitting = false,
  onActiveTabChange,
  schedulingMethod,
  initialDate,
}: ApplicationSignupContainerProps) {
  const { t } = useTranslation();
  const [activeTabIndex, setActiveTabIndex] = useState(() =>
    getInitialTabIndex(dateGroups, initialDate),
  );

  // dateGroups load asynchronously, so the initial date may not be present on
  // the first render. Apply it once, when the matching group first appears.
  const hasAppliedInitialDate = useRef(false);
  useEffect(() => {
    if (hasAppliedInitialDate.current || !initialDate || dateGroups.length === 0) {
      return;
    }
    const index = dateGroups.findIndex((group) => group.date === initialDate);
    if (index >= 0) {
      hasAppliedInitialDate.current = true;
      setActiveTabIndex(index);
      onActiveTabChange?.(index);
    }
  }, [dateGroups, initialDate, onActiveTabChange]);

  // Reset active tab if it's out of bounds when dateGroups change
  useEffect(() => {
    if (activeTabIndex >= dateGroups.length && dateGroups.length > 0) {
      setActiveTabIndex(0);
      onActiveTabChange?.(0);
    }
  }, [dateGroups.length, activeTabIndex, onActiveTabChange]);

  // Memoize active group to prevent unnecessary re-renders
  const activeGroup = useMemo(() => dateGroups[activeTabIndex], [dateGroups, activeTabIndex]);

  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    onActiveTabChange?.(index);
  };

  const handleShiftToggle = (shiftId: string, checked: boolean) => {
    if (checked) {
      // Find the shift being toggled to get its date
      const shift = activeGroup?.shifts.find((s) => s.id === shiftId);
      if (!shift) {
        return;
      }

      // Prevent duplicate selections
      if (selectedShiftIds.includes(shiftId)) {
        return;
      }

      // Remove any other selections from the SAME date (only one shift per date allowed)
      const otherDatesSelections = selectedShiftIds.filter((id) => {
        // Find which date this selected shift belongs to
        const selectedShift = dateGroups.flatMap((group) => group.shifts).find((s) => s.id === id);

        // Keep selections from different dates only
        return selectedShift && selectedShift.date !== shift.date;
      });

      onSelectionChange([...otherDatesSelections, shiftId]);
    } else {
      onSelectionChange(selectedShiftIds.filter((id) => id !== shiftId));
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-28">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <h2 className="text-lg font-semibold text-txt-primary">
            {getSignupTitle(t, schedulingMethod)}
          </h2>
          {onHelpClick && (
            <button
              type="button"
              onClick={onHelpClick}
              aria-label={t("common:a11y.explain")}
              className="touch-target flex h-6 w-6 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-xl text-txt-tertiary" />
            </button>
          )}
        </div>

        {/* Date tabs - Segmented control */}
        <div className="no-scrollbar -mx-4 flex flex-nowrap gap-4 overflow-x-auto border-b border-bordercolor-primary px-4 pb-0">
          {dateGroups.map((group, index) => (
            <ApplicationSignupDateTab
              key={group.date}
              label={group.label}
              count={group.shifts.length}
              claimed={!!group.claim}
              isActive={activeTabIndex === index}
              onClick={() => handleTabChange(index)}
            />
          ))}
        </div>
      </div>

      {/* Shift boxes */}
      {activeGroup && (
        <div className="flex flex-col gap-3" key={`tab-${activeTabIndex}-${activeGroup.date}`}>
          {/* Een dag die al van jou is blijft staan, met deze regel erboven. Hij verdween eerder
              onder je handen: je meldde je aan voor dag 1, de dag viel uit de balk en je stond op
              dag 2 -- alsof je aanmelding nooit was aangekomen. */}
          {activeGroup.claim && (
            <p className="text-sm font-medium text-txt-secondary">
              {t(`common:applications.claim.day.${claimTone(activeGroup.claim)}`)}
            </p>
          )}

          {activeGroup.shifts.map((shift) => (
            <ApplicationSignupShiftBox
              key={`${activeGroup.date}-${shift.id}`}
              title={shift.title}
              timeRange={shift.timeRange}
              checked={selectedShiftIds.includes(shift.id)}
              onCheckedChange={(checked) => handleShiftToggle(shift.id, checked)}
              readOnly={!!activeGroup.claim}
              claimLabel={
                shift.claim ? t(`common:applications.claim.status.${shift.claim}`) : undefined
              }
            >
              {shift.isReserveSlot && !activeGroup.claim && (
                <p className="text-sm font-medium text-warning-120">Je meldt je aan als reserve</p>
              )}
            </ApplicationSignupShiftBox>
          ))}
        </div>
      )}
    </div>
  );
}
